import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { SectionCard } from './SectionCard';
import { SettingsSection } from './SettingsSection';
import { useNotificationTemplates, useUpdateTemplate, useDeleteTemplate } from '../hooks/useNotificationTemplates';
import type { PushNotificationTemplate } from '../api/notificationTemplatesApi';

export const SavedTemplatesCard: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [editDialog, setEditDialog] = useState<PushNotificationTemplate | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<PushNotificationTemplate | null>(null);
  const [editForm, setEditForm] = useState({ name: '', title: '', body: '' });

  const { data, isLoading, error } = useNotificationTemplates({
    page: page + 1,
    page_size: pageSize,
    search: debouncedSearch || undefined,
  });

  const updateMutation = useUpdateTemplate();
  const deleteMutation = useDeleteTemplate();

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const templates = data?.data?.templates ?? [];
  const total = data?.data?.total ?? 0;

  const handleEditOpen = (t: PushNotificationTemplate) => {
    setEditForm({ name: t.name, title: t.title, body: t.body });
    setEditDialog(t);
  };

  const handleEditSave = () => {
    if (!editDialog) return;
    updateMutation.mutate({ id: editDialog.id, ...editForm }, { onSuccess: () => setEditDialog(null) });
  };

  const handleDeleteConfirm = () => {
    if (!deleteDialog) return;
    deleteMutation.mutate(deleteDialog.id, {
      onSuccess: () => setDeleteDialog(null),
    });
  };

  return (
    <SettingsSection>
      <SectionCard title='Saved Templates'>
        <Stack spacing={2}>
          <TextField
            label='Search templates'
            size='small'
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ maxWidth: 300 }}
          />

          {error && <Alert severity='error'>Failed to load templates.</Alert>}

          {isLoading ? (
            <Box display='flex' justifyContent='center' py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : templates.length === 0 ? (
            <Typography variant='body2' color='text.secondary' py={2}>
              No templates saved yet.
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align='right'>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell>{t.name}</TableCell>
                        <TableCell
                          sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {t.title}
                        </TableCell>
                        <TableCell>{new Date(t.created_at).toLocaleDateString()}</TableCell>
                        <TableCell align='right'>
                          <IconButton size='small' onClick={() => handleEditOpen(t)}>
                            <EditIcon fontSize='small' />
                          </IconButton>
                          <IconButton size='small' onClick={() => setDeleteDialog(t)}>
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component='div'
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            </>
          )}
        </Stack>
      </SectionCard>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth='sm' fullWidth>
        <DialogTitle>Edit Template</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label='Template Name'
              size='small'
              fullWidth
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label='Title'
              size='small'
              fullWidth
              value={editForm.title}
              onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              label='Message'
              size='small'
              fullWidth
              multiline
              minRows={3}
              value={editForm.body}
              onChange={(e) => setEditForm((f) => ({ ...f, body: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleEditSave}
            disabled={
              !editForm.name.trim() || !editForm.title.trim() || !editForm.body.trim() || updateMutation.isPending
            }
          >
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete "{deleteDialog?.name}"? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button color='error' variant='contained' onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsSection>
  );
};
